import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from '@mui/material';
import { useNotification } from './context/NotificationContext';

const NOTIF_API_BASE = process.env.REACT_APP_NOTIF_API_BASE || 'https://frek.cc/proxy/10.147.19.189:5006';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: '#0c0d12', paper: '#14171f' },
    text: {
      primary: '#e8e9ee',
      secondary: 'rgba(232,233,238,0.7)',
    },
    primary: { main: '#6aa9ff' },
  },
  typography: {
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
});

const tableHeadCellSx = {
  fontWeight: 700,
  color: '#d9e6ff',
  backgroundColor: 'rgba(30, 35, 52, 0.92)',
  borderBottom: '1px solid rgba(255,255,255,0.12)',
  textAlign: 'center',
};

function buildCsv(numbers) {
  const header = 'number';
  const rows = numbers.map((value) => `"${String(value).replace(/"/g, '""')}"`);
  return [header, ...rows].join('\n');
}

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const payload = await response.json();
      return payload.error || `Request failed (${response.status})`;
    }
    const text = await response.text();
    return text || `Request failed (${response.status})`;
  } catch (error) {
    return `Request failed (${response.status})`;
  }
}

function CatchedNumbers() {
  const { showNotification } = useNotification();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dayNumbers, setDayNumbers] = useState([]);
  const [monthDays, setMonthDays] = useState([]);
  const [loadingDay, setLoadingDay] = useState(false);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [deletingDay, setDeletingDay] = useState(false);
  const [deletingMonth, setDeletingMonth] = useState(false);
  const [confirmDeleteTarget, setConfirmDeleteTarget] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastFetchedLabel, setLastFetchedLabel] = useState('');

  const notify = (severity, message) => {
    showNotification(message, severity, {
      autoHideDuration: 2800,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
  };

  const monthUniqueNumbers = useMemo(() => {
    const merged = new Set();
    monthDays.forEach((dayEntry) => {
      (dayEntry.numbers || []).forEach((num) => merged.add(String(num).trim()));
    });
    return Array.from(merged).filter((num) => num.length > 0).sort();
  }, [monthDays]);

  const handleFetchDay = async () => {
    setLoadingDay(true);
    setError('');
    setSuccess('');
    notify('info', `Fetching day data for ${date}...`);
    try {
      const response = await fetch(
        `${NOTIF_API_BASE}/catched-numbers/day?date=${encodeURIComponent(date)}`
      );
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const payload = await response.json();
      const numbers = Array.isArray(payload.numbers)
        ? payload.numbers.map((num) => String(num).trim()).filter((num) => num.length > 0)
        : [];
      setDayNumbers(Array.from(new Set(numbers)).sort());
      setLastFetchedLabel(`Day ${date}`);
      notify('success', `Fetched ${numbers.length} numbers for ${date}.`);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to fetch day numbers.');
      setDayNumbers([]);
      notify('error', fetchError.message || 'Failed to fetch day numbers.');
    } finally {
      setLoadingDay(false);
    }
  };

  const handleFetchMonth = async () => {
    setLoadingMonth(true);
    setError('');
    setSuccess('');
    notify('info', `Fetching month data for ${month}...`);
    try {
      const [year, monthNumber] = month.split('-');
      const response = await fetch(
        `${NOTIF_API_BASE}/catched-numbers/month?year=${encodeURIComponent(year)}&month=${encodeURIComponent(monthNumber)}`
      );
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      const payload = await response.json();
      setMonthDays(Array.isArray(payload.days) ? payload.days : []);
      setLastFetchedLabel(`Month ${month}`);
      notify('success', `Fetched ${Array.isArray(payload.days) ? payload.days.length : 0} day entries for ${month}.`);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to fetch month numbers.');
      setMonthDays([]);
      notify('error', fetchError.message || 'Failed to fetch month numbers.');
    } finally {
      setLoadingMonth(false);
    }
  };

  const handleDeleteDay = async () => {
    setDeletingDay(true);
    setError('');
    setSuccess('');
    notify('info', `Deleting day data for ${date}...`);
    try {
      const response = await fetch(
        `${NOTIF_API_BASE}/catched-numbers/day?date=${encodeURIComponent(date)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const payload = await response.json();
      setSuccess(payload.message || `Deleted day data for ${date}.`);
      setDayNumbers([]);
      setMonthDays((prev) => prev.filter((entry) => entry.date !== date));
      setLastFetchedLabel(`Deleted Day ${date}`);
      notify('success', payload.message || `Deleted day data for ${date}.`);
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete day numbers.');
      notify('error', deleteError.message || 'Failed to delete day numbers.');
    } finally {
      setDeletingDay(false);
    }
  };

  const handleDeleteMonth = async () => {
    const [year, monthNumber] = month.split('-');
    setDeletingMonth(true);
    setError('');
    setSuccess('');
    notify('info', `Deleting month data for ${month}...`);
    try {
      const response = await fetch(
        `${NOTIF_API_BASE}/catched-numbers/month?year=${encodeURIComponent(year)}&month=${encodeURIComponent(monthNumber)}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      const payload = await response.json();
      setSuccess(payload.message || `Deleted month data for ${month}.`);
      setMonthDays([]);
      if (date.startsWith(`${month}-`)) {
        setDayNumbers([]);
      }
      setLastFetchedLabel(`Deleted Month ${month}`);
      notify('success', payload.message || `Deleted month data for ${month}.`);
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete month numbers.');
      notify('error', deleteError.message || 'Failed to delete month numbers.');
    } finally {
      setDeletingMonth(false);
    }
  };

  const openDeleteDialog = (target) => {
    setConfirmDeleteTarget(target);
    setConfirmDeleteOpen(true);
  };

  const closeDeleteDialog = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    setConfirmDeleteOpen(false);
    if (confirmDeleteTarget === 'day') {
      await handleDeleteDay();
      return;
    }
    if (confirmDeleteTarget === 'month') {
      await handleDeleteMonth();
    }
  };

  const exportDayTxt = () => {
    downloadFile(`${date}-catched-numbers.txt`, dayNumbers.join('\n'), 'text/plain;charset=utf-8');
    notify('success', `Exported TXT for ${date}.`);
  };

  const exportDayCsv = () => {
    downloadFile(`${date}-catched-numbers.csv`, buildCsv(dayNumbers), 'text/csv;charset=utf-8');
    notify('success', `Exported CSV for ${date}.`);
  };

  const exportMonthTxt = () => {
    downloadFile(`${month}-catched-numbers.txt`, monthUniqueNumbers.join('\n'), 'text/plain;charset=utf-8');
    notify('success', `Exported TXT for ${month}.`);
  };

  const exportMonthCsv = () => {
    downloadFile(
      `${month}-catched-numbers.csv`,
      buildCsv(monthUniqueNumbers),
      'text/csv;charset=utf-8'
    );
    notify('success', `Exported CSV for ${month}.`);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background:
            'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
            'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
            'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
          color: '#e8e9ee',
          py: 2,
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'rgba(18, 18, 22, 0.75)',
                border: '1px solid rgba(255,255,255,0.06)',
                backdropFilter: 'blur(6px)',
              }}
            >
              <Stack spacing={1.2}>
                <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                  Catched Numbers
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Chip
                    label={`Day Total ${dayNumbers.length.toLocaleString()}`}
                    sx={{ bgcolor: 'rgba(106,169,255,0.2)', color: '#d5e7ff', fontWeight: 700 }}
                  />
                  <Chip
                    label={`Month Unique ${monthUniqueNumbers.length.toLocaleString()}`}
                    sx={{ bgcolor: 'rgba(90,210,122,0.18)', color: '#b9f2c8', fontWeight: 700 }}
                  />
                  <Chip
                    label={lastFetchedLabel || 'No data fetched yet'}
                    sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: '#e8e9ee', fontWeight: 700 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            {error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {success}
              </Alert>
            ) : null}

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

            <Grid container spacing={1.5}>
              <Grid item xs={12} lg={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    background: 'rgba(18, 18, 22, 0.75)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Day Retrieval</Typography>
                      <TextField
                        label="Date"
                        type="date"
                        value={date}
                        onChange={(event) => setDate(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Button variant="contained" onClick={handleFetchDay} disabled={loadingDay}>
                          {loadingDay ? 'Loading...' : 'Fetch Day'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => openDeleteDialog('day')}
                          disabled={deletingDay}
                        >
                          {deletingDay ? 'Deleting...' : 'Delete Day'}
                        </Button>
                        <Button variant="outlined" onClick={exportDayTxt} disabled={dayNumbers.length === 0}>
                          Export TXT
                        </Button>
                        <Button variant="outlined" onClick={exportDayCsv} disabled={dayNumbers.length === 0}>
                          Export CSV
                        </Button>
                      </Stack>
                      <TextField
                        label="Numbers"
                        value={dayNumbers.join('\n')}
                        multiline
                        minRows={12}
                        fullWidth
                        InputProps={{ readOnly: true }}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={6}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    background: 'rgba(18, 18, 22, 0.75)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontWeight: 700, color: '#d9e6ff' }}>Month Retrieval</Typography>
                      <TextField
                        label="Month"
                        type="month"
                        value={month}
                        onChange={(event) => setMonth(event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Button variant="contained" onClick={handleFetchMonth} disabled={loadingMonth}>
                          {loadingMonth ? 'Loading...' : 'Fetch Month'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => openDeleteDialog('month')}
                          disabled={deletingMonth}
                        >
                          {deletingMonth ? 'Deleting...' : 'Delete Month'}
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={exportMonthTxt}
                          disabled={monthUniqueNumbers.length === 0}
                        >
                          Export TXT
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={exportMonthCsv}
                          disabled={monthUniqueNumbers.length === 0}
                        >
                          Export CSV
                        </Button>
                      </Stack>
                      <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={tableHeadCellSx}>Date</TableCell>
                              <TableCell sx={tableHeadCellSx}>Count</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {monthDays.map((dayRow) => (
                              <TableRow
                                key={dayRow.date}
                                sx={{
                                  '& td': { borderColor: 'rgba(255,255,255,0.08)' },
                                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' },
                                }}
                              >
                                <TableCell align="center">{dayRow.date}</TableCell>
                                <TableCell align="center">{(dayRow.numbers || []).length}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ '& td': { borderColor: 'rgba(255,255,255,0.08)', fontWeight: 700 } }}>
                              <TableCell align="center">Unique Total</TableCell>
                              <TableCell align="center">{monthUniqueNumbers.length}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Stack>
        </Container>
        <Dialog
          open={confirmDeleteOpen}
          onClose={closeDeleteDialog}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {confirmDeleteTarget === 'day'
                ? `Delete all catched numbers for ${date}? This cannot be undone.`
                : `Delete all catched numbers for ${month}? This cannot be undone.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}

export default CatchedNumbers;
